"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { siteConfig } from "@/config/site";
import { getScriptarioLocaleFromPath, getScriptarioLocalizedPath, scriptarioLocales } from "@/config/locales";

interface LanguageSwitcherProps {
  className?: string;
  onSelect?: () => void;
}

function CaretDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3.5 5.25 7 8.75l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LanguageSwitcher({ className = "", onSelect }: LanguageSwitcherProps) {
  const pathname = usePathname() || "/";
  const switcherRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (siteConfig.siteId !== "scriptario") {
    return null;
  }

  const currentLocaleCode = getScriptarioLocaleFromPath(pathname);
  const currentLocale = scriptarioLocales.find((locale) => locale.code === currentLocaleCode) ?? scriptarioLocales[0];
  const languageLabels: Record<string, string> = {
    en: "Select language",
    es: "Seleccionar idioma",
    "pt-br": "Selecionar idioma",
    it: "Seleziona lingua",
    de: "Sprache auswählen",
  };

  return (
    <div ref={switcherRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={languageLabels[currentLocaleCode] ?? languageLabels.en}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-[40px] min-w-[116px] items-center justify-between gap-[8px] whitespace-nowrap rounded-[12px] border border-[#DDE7F6] bg-white px-[12px] text-[14px] font-semibold text-[#1A1D1F] shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#F7FAFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/40"
      >
        <span className="inline-flex items-center gap-[8px] whitespace-nowrap">
          <span aria-hidden="true" className="shrink-0 text-[16px] leading-none">{currentLocale.flag}</span>
          <span className="whitespace-nowrap">{currentLocale.shortLabel}</span>
        </span>
        <span
          aria-hidden="true"
          className={`inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center text-[#64748B] transition-transform ${open ? "rotate-180" : ""}`}
        >
          <CaretDownIcon />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[46px] z-[110] min-w-[232px] rounded-[12px] border border-[#DDE7F6] bg-white p-[6px] shadow-[0_14px_34px_rgba(15,23,42,0.14)]"
        >
          {scriptarioLocales.map((locale) => (
            <Link
              key={locale.code}
              role="menuitem"
              aria-current={locale.code === currentLocaleCode ? "true" : undefined}
              href={getScriptarioLocalizedPath(pathname, locale.code)}
              className={`flex items-center justify-between gap-[14px] whitespace-nowrap rounded-[8px] px-[10px] py-[9px] text-[14px] font-medium text-[#1A1D1F] transition-colors hover:bg-[var(--brand-primary-tint)] ${locale.code === currentLocaleCode ? "bg-[var(--brand-primary-tint)]" : ""}`}
              onClick={() => {
                setOpen(false);
                onSelect?.();
              }}
            >
              <span className="inline-flex min-w-0 items-center gap-[9px]">
                <span aria-hidden="true" className="shrink-0 text-[16px] leading-none">{locale.flag}</span>
                <span className="truncate">{locale.label}</span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-[12px] font-semibold text-[var(--brand-primary)]">{locale.shortLabel}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
